import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { KeyRound, LayoutGrid, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  SkeletonList,
  useToast,
} from '../../components/ui';
import { ApiError } from '../../services/api';
import {
  useAplicacionesDePersona,
  useCambiarContrasena,
  useCambiarEstado,
  useFijarAplicaciones,
  usePersonas,
  type AplicacionDeLaPersona,
  type PersonaDelEcosistema,
} from '../../services/users.service';

/**
 * Las personas del ecosistema: quien es cada una, que aplicaciones ve y su
 * contrasena.
 *
 * ## Por que existe
 *
 * Porque antes no habia ningun sitio para esto: crear a alguien o cambiarle la
 * contrasena obligaba a entrar en la consola de Keycloak, que es una
 * herramienta de administracion de identidades entera —realms, clientes,
 * mapeadores, flujos— para hacer dos cosas. Quien administra el ecosistema no
 * tiene por que pasar por ahi.
 *
 * ## Lo que se ve y de donde sale
 *
 * Las personas salen de Keycloak, que es donde viven. Las aplicaciones se
 * conceden aqui mismo, una a una: no hay un rol «usuario» que abra un paquete
 * de aplicaciones, porque dos personas del mismo area acaban necesitando cosas
 * distintas y eso obliga a inventar un rol nuevo por cada combinacion. Se entra
 * a la persona y se marca lo suyo.
 *
 * Quien administra el ecosistema las ve todas siempre, con concesiones o sin
 * ellas; por eso sus casillas se enseñan pero no deciden nada.
 */
export function AdminUsersPage(): JSX.Element {
  const toast = useToast();
  const { data: personas, isLoading, isError, refetch } = usePersonas();

  const [busqueda, setBusqueda] = useState('');
  const [cambiando, setCambiando] = useState<PersonaDelEcosistema | null>(null);
  const [repartiendo, setRepartiendo] = useState<PersonaDelEcosistema | null>(null);
  const [porDesactivar, setPorDesactivar] = useState<PersonaDelEcosistema | null>(null);

  const cambiarContrasena = useCambiarContrasena();
  const cambiarEstado = useCambiarEstado();

  const visibles = useMemo(() => {
    const aguja = busqueda.trim().toLowerCase();
    if (!aguja) return personas ?? [];
    return (personas ?? []).filter((p) =>
      [p.nombre, p.correo ?? '', p.usuario, ...p.roles].some((campo) =>
        campo.toLowerCase().includes(aguja),
      ),
    );
  }, [personas, busqueda]);

  const alternarEstado = async (persona: PersonaDelEcosistema, activa: boolean) => {
    try {
      await cambiarEstado.mutateAsync({ id: persona.id, activa });
      toast.success(
        activa
          ? `${persona.nombre} vuelve a tener acceso`
          : `Cuenta de ${persona.nombre} desactivada; cerrando sesiones`,
      );
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'No se pudo cambiar el estado.');
    } finally {
      setPorDesactivar(null);
    }
  };

  if (isLoading) return <SkeletonList />;
  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Personas"
        description="Quién entra al ecosistema, qué aplicaciones ve y su contraseña."
      />

      <Card>
        <CardHeader
          title={`${visibles.length} ${visibles.length === 1 ? 'persona' : 'personas'}`}
          description="Los datos salen de ED Ecosystem; las aplicaciones se calculan con las reglas de acceso."
        />
        <CardBody className="space-y-4">
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, correo o rol"
            aria-label="Buscar personas"
          />

          {visibles.length === 0 ? (
            <EmptyState title="Nadie coincide con esa búsqueda" />
          ) : (
            <ul className="divide-y divide-slate-200">
              {visibles.map((persona) => (
                <li key={persona.id} className="flex flex-wrap items-start gap-3 py-3">
                  <div className="min-w-[220px] flex-1">
                    <p className="font-medium text-slate-900">
                      {persona.nombre}
                      {!persona.activa && (
                        <Badge tone="danger" className="ml-2">
                          sin acceso
                        </Badge>
                      )}
                    </p>
                    <p className="text-sm text-slate-500">{persona.correo ?? persona.usuario}</p>
                  </div>

                  <div className="min-w-[160px] flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      Roles
                    </p>
                    <p className="text-sm text-slate-600">{persona.roles.join(', ') || '—'}</p>
                  </div>

                  <div className="min-w-[200px] flex-[1.4]">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      Aplicaciones que ve
                    </p>
                    <p className="text-sm text-slate-600">
                      {persona.aplicaciones.join(', ') || 'ninguna'}
                    </p>
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setRepartiendo(persona)}>
                      <LayoutGrid size={14} /> Aplicaciones
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setCambiando(persona)}>
                      <KeyRound size={14} /> Contraseña
                    </Button>
                    {persona.activa ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPorDesactivar(persona)}
                      >
                        <ShieldOff size={14} /> Quitar acceso
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void alternarEstado(persona, true)}
                      >
                        <ShieldCheck size={14} /> Devolver acceso
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {cambiando && (
        <DialogoDeContrasena
          persona={cambiando}
          enviando={cambiarContrasena.isPending}
          onCerrar={() => setCambiando(null)}
          onGuardar={async (contrasena, temporal) => {
            try {
              await cambiarContrasena.mutateAsync({ id: cambiando.id, contrasena, temporal });
              toast.success(`Contraseña cambiada a ${cambiando.nombre}`);
              setCambiando(null);
            } catch (error) {
              toast.error(
                error instanceof ApiError ? error.message : 'No se pudo cambiar la contraseña.',
              );
            }
          }}
        />
      )}

      {repartiendo && (
        <DialogoDeAplicaciones
          key={repartiendo.id}
          persona={repartiendo}
          onCerrar={() => setRepartiendo(null)}
        />
      )}

      <ConfirmDialog
        open={porDesactivar !== null}
        title="Quitar el acceso"
        message={
          porDesactivar
            ? `La cuenta de ${porDesactivar.nombre} quedará desactivada y se cerrarán sus sesiones de inicio de sesión. En Ecosystem perderá acceso en un máximo de 15 segundos.`
            : ''
        }
        confirmLabel="Quitar el acceso"
        onCancel={() => setPorDesactivar(null)}
        onConfirm={() => {
          if (porDesactivar) void alternarEstado(porDesactivar, false);
        }}
      />
    </div>
  );
}

/**
 * El formulario de contraseña.
 *
 * «Que la cambie al entrar» viene marcado a propósito. Una contraseña que se
 * reparte por teléfono o por chat la acaban sabiendo dos personas; marcándolo,
 * deja de valer en cuanto la usan y quien administra no se queda sabiendo la
 * contraseña de nadie. Se puede desmarcar, porque a veces hace falta dejarla
 * puesta, pero que sea una decisión y no el camino por defecto.
 */
function DialogoDeContrasena({
  persona,
  enviando,
  onCerrar,
  onGuardar,
}: {
  persona: PersonaDelEcosistema;
  enviando: boolean;
  onCerrar: () => void;
  onGuardar: (contrasena: string, temporal: boolean) => void | Promise<void>;
}): JSX.Element {
  const [contrasena, setContrasena] = useState('');
  const [temporal, setTemporal] = useState(true);

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (contrasena.trim().length < 6) return;
    void onGuardar(contrasena, temporal);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <form
        onSubmit={enviar}
        className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
      >
        <h2 className="text-lg font-semibold text-slate-900">Contraseña de {persona.nombre}</h2>
        <p className="mt-1 text-sm text-slate-500">{persona.correo ?? persona.usuario}</p>

        <Input
          className="mt-4"
          type="password"
          autoFocus
          autoComplete="new-password"
          value={contrasena}
          onChange={(e) => setContrasena(e.target.value)}
          placeholder="Nueva contraseña"
          aria-label="Nueva contraseña"
        />
        <p className="mt-1 text-[12px] text-slate-400">
          Al menos 6 caracteres. Al guardar, se cerrarán las sesiones anteriores.
        </p>

        <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={temporal}
            onChange={(e) => setTemporal(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Que la cambie al entrar
            <span className="block text-[12px] text-slate-400">
              Así esta contraseña deja de valer en cuanto la use.
            </span>
          </span>
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button type="submit" disabled={enviando || contrasena.trim().length < 6}>
            {enviando ? 'Guardando…' : 'Cambiar'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Las aplicaciones de una persona.
 *
 * Se marca lo que debe tener y se guarda de una vez. Se manda la lista entera
 * y no los cambios sueltos: asi lo que queda guardado es exactamente lo que se
 * estaba viendo, aunque otra pestaña haya tocado a la misma persona mientras
 * tanto.
 *
 * El botón queda apagado mientras no haya nada distinto que guardar. Es lo que
 * evita el guardado por inercia —abrir, mirar y darle a Guardar— que es como se
 * pierde un acceso sin enterarse.
 */
export function DialogoDeAplicaciones({
  persona,
  onCerrar,
}: {
  persona: PersonaDelEcosistema;
  onCerrar: () => void;
}): JSX.Element {
  const toast = useToast();
  const { data: datos, isLoading, isError, refetch } = useAplicacionesDePersona(persona.id);
  const fijar = useFijarAplicaciones();

  const [marcadas, setMarcadas] = useState<Set<string> | null>(null);

  const [base, setBase] = useState<typeof datos>(undefined);
  const [conflicto, setConflicto] = useState(false);
  const [actuales, setActuales] = useState<typeof datos>(undefined);
  const [cargandoActuales, setCargandoActuales] = useState(false);
  const aplicaciones = base?.aplicaciones;
  useEffect(() => {
    if (datos && !base) {
      setBase(datos);
      setMarcadas(new Set(datos.aplicaciones.filter((a) => a.tiene).map((a) => a.id)));
    }
  }, [datos, base]);
  const cargarActuales = async (): Promise<void> => {
    setCargandoActuales(true);
    const resultado = await refetch();
    setCargandoActuales(false);
    if (resultado.isError || !resultado.data) {
      toast.error('No se pudieron cargar los accesos actuales. Tu selección sigue aquí.');
      return;
    }
    setActuales(resultado.data);
  };

  const esAdministradora = persona.roles.includes('SUPER_ADMIN');

  const hayCambios = useMemo(() => {
    if (!aplicaciones || !marcadas) return false;
    const guardadas = aplicaciones.filter((a) => a.tiene).map((a) => a.id);
    return guardadas.length !== marcadas.size || guardadas.some((id) => !marcadas.has(id));
  }, [aplicaciones, marcadas]);

  const alternar = (id: string): void => {
    setMarcadas((previas) => {
      const siguiente = new Set(previas ?? []);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });
  };

  const guardar = async (): Promise<void> => {
    if (!marcadas || !base || conflicto) return;
    try {
      await fijar.mutateAsync({
        id: persona.id,
        aplicaciones: [...marcadas],
        expectedRevision: base.revision,
      });
      toast.success(`Aplicaciones de ${persona.nombre} actualizadas`);
      onCerrar();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ACCESS_REVISION_CONFLICT') {
        setConflicto(true);
        setActuales(undefined);
        await cargarActuales();
      }
      toast.error(
        error instanceof ApiError ? error.message : 'No se pudieron guardar las aplicaciones.',
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 p-5">
          <h2 className="text-lg font-semibold text-slate-900">Aplicaciones de {persona.nombre}</h2>
          <p className="mt-1 text-sm text-slate-500">{persona.correo ?? persona.usuario}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {esAdministradora && (
            <p className="mb-4 rounded-lg bg-amber-50 p-3 text-[13px] text-amber-900">
              Es super administradora: entra a todas las aplicaciones aunque aquí no haya nada
              marcado.
            </p>
          )}

          {isLoading && <SkeletonList rows={4} />}
          {isError && <ErrorState onRetry={() => void refetch()} />}

          {conflicto && (
            <div role="alert" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-950">
              <p>Otra persona cambió los accesos. Tu selección no se ha guardado ni descartado.</p>
              {actuales ? (
                <>
                  <p className="mt-2">Compara tu selección con los accesos actuales:</p>
                  <ul>
                    {actuales.aplicaciones.map((a) => (
                      <li key={a.id}>
                        {a.nombre}: actual {a.tiene ? 'sí' : 'no'} · tu selección{' '}
                        {marcadas?.has(a.id) ? 'sí' : 'no'}
                      </li>
                    ))}
                  </ul>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setBase(actuales);
                      setMarcadas(
                        new Set(actuales.aplicaciones.filter((a) => a.tiene).map((a) => a.id)),
                      );
                      setConflicto(false);
                    }}
                  >
                    Descartar selección y cargar actuales
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setBase(actuales);
                      setConflicto(false);
                    }}
                  >
                    Ya comparé: conservar mi selección para guardar
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  disabled={cargandoActuales}
                  onClick={() => void cargarActuales()}
                >
                  {cargandoActuales ? 'Cargando accesos actuales…' : 'Reintentar comparación'}
                </Button>
              )}
            </div>
          )}
          {aplicaciones && marcadas && (
            <ul className="space-y-1">
              {aplicaciones.map((aplicacion) => (
                <li key={aplicacion.id}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      disabled={fijar.isPending}
                      checked={marcadas.has(aplicacion.id)}
                      onChange={() => alternar(aplicacion.id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-slate-900">{aplicacion.nombre}</span>
                      <span className="block font-mono text-[12px] text-slate-400">
                        {aplicacion.slug}
                      </span>
                    </span>
                    {aplicacion.estado !== 'ACTIVE' && (
                      <Badge tone="neutral">{etiquetaDeEstado(aplicacion)}</Badge>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-5">
          <Button type="button" variant="secondary" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!hayCambios || fijar.isPending || conflicto}
            onClick={() => void guardar()}
          >
            {fijar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Por que se avisa del estado.
 *
 * Una aplicacion inactiva no la ve nadie, tenga o no concesion. Sin este aviso,
 * marcarla parece que ha funcionado y la persona sigue sin verla.
 */
function etiquetaDeEstado(aplicacion: AplicacionDeLaPersona): string {
  if (aplicacion.estado === 'INACTIVE') return 'inactiva: no la verá';
  if (aplicacion.estado === 'MAINTENANCE') return 'en mantenimiento';
  return 'próximamente';
}
